import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";

// Busca, paginação e ordenação no servidor, no padrão da tela de Integração ERP.
// `filtros` precisa ter identidade estável (constante fora do componente ou useMemo).
export default function useClientesPaginados(
  filtros,
  { ativo = true, tamanhoInicial = 100 } = {},
) {
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(tamanhoInicial);
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: "asc" });
  const [linhas, setLinhas] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const controllerRef = useRef(null);

  useEffect(() => {
    const temporizador = setTimeout(() => {
      setBuscaAplicada(busca.trim());
      setPagina(0);
    }, 400);
    return () => clearTimeout(temporizador);
  }, [busca]);

  const buscar = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setCarregando(true);

    try {
      const resposta = await api.post(
        "/interno/find/clientesPaginado",
        {
          ...filtros,
          busca: buscaAplicada || undefined,
          pagina,
          tamanho: linhasPorPagina,
          ordenarPor: ordenacao.coluna || undefined,
          direcao: ordenacao.coluna ? ordenacao.direcao : undefined,
        },
        { signal: controller.signal },
      );
      setLinhas(resposta?.data?.clientes || []);
      setTotal(resposta?.data?.total || 0);
    } catch (error) {
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return;
      }
      console.error(error);
    } finally {
      if (controllerRef.current === controller) setCarregando(false);
    }
  }, [filtros, buscaAplicada, pagina, linhasPorPagina, ordenacao]);

  useEffect(() => {
    if (ativo) buscar();
  }, [ativo, buscar]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const reiniciar = useCallback(() => {
    setBusca("");
    setBuscaAplicada("");
    setPagina(0);
  }, []);

  const modeloOrdenacao = useMemo(
    () =>
      ordenacao.coluna
        ? [{ field: ordenacao.coluna, sort: ordenacao.direcao }]
        : [],
    [ordenacao],
  );

  const opcoesGrid = {
    paginationMode: "server",
    rowCount: total,
    paginationModel: { page: pagina, pageSize: linhasPorPagina },
    onPaginationModelChange: (modelo) => {
      if (modelo.pageSize !== linhasPorPagina) {
        setLinhasPorPagina(modelo.pageSize);
        setPagina(0);
      } else {
        setPagina(modelo.page);
      }
    },
    sortingMode: "server",
    sortModel: modeloOrdenacao,
    onSortModelChange: (modelo) => {
      setOrdenacao({
        coluna: modelo[0]?.field || null,
        direcao: modelo[0]?.sort || "asc",
      });
      setPagina(0);
    },
  };

  return {
    busca,
    setBusca,
    linhas,
    total,
    carregando,
    recarregar: buscar,
    reiniciar,
    opcoesGrid,
  };
}
